import { components as c } from "@/components";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import { useClinicianOptions } from "@/features/auth/api/cliniciansByOrganisationQuery";
import { StaffUserComboBox } from "@/features/profile/components/StaffUserComboBox";
import InviteForm from "@/features/invite/components/InviteForm";
import { LockableField, LockableFieldLabel } from "@/features/templates/components/TemplateScopeLock";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateLocks } from "@/features/templates/hooks/useTemplateLocks";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { templateSettingsSchema } from "@patient-watch/types";
import { isUuid } from "@/services/isUuid";
import { services as s } from "@/services";
import { useAuth } from "@/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const shockwaveFormSchema = z.object({
    referring_clinician_id: z.string().uuid().optional().nullable(),
    shockwaving_clinician_id: z.string().uuid().optional().nullable(),
});

type ShockwaveFormValues = z.infer<typeof shockwaveFormSchema>;

const ShockwaveFormFieldsPage = () => {
    const auth = useAuth();
    const { templateId } = Route.useParams();
    const referringLocked = useTemplateLocks(templateId).isLocked("referring_clinician_id");
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());
    const referring_clinician_id = search.referring_clinician_id;
    const shockwaving_clinician_id = search.shockwaving_clinician_id;

    const { markStepComplete, nextStep, setCurrentStep, getNextStepRoute, getPreviousStepRoute, currentStep } =
        useTemplateNavigationStore();
    const navigate = useNavigate();

    const form = useForm<ShockwaveFormValues>({
        resolver: zodResolver(shockwaveFormSchema),
        defaultValues: {
            referring_clinician_id: "",
            shockwaving_clinician_id: "",
        },
    });

    const clinicianOptions = useClinicianOptions();

    useEffect(() => {
        setCurrentStep("shockwave");
    }, [setCurrentStep]);

    // Set form values after options load - using reset to ensure ComboBox recognizes values
    useEffect(() => {
        if (clinicianOptions.length > 0) {
            // Set form values from search params or default to user's own ID
            const referringValue = isUuid(referring_clinician_id)
                ? referring_clinician_id
                : referringLocked
                  ? ""
                  : auth?.id || "";
            const shockwavingValue = isUuid(shockwaving_clinician_id) ? shockwaving_clinician_id : auth?.id || "";

            form.reset({
                referring_clinician_id: referringValue,
                shockwaving_clinician_id: shockwavingValue,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicianOptions.length, referring_clinician_id, shockwaving_clinician_id, auth?.id, referringLocked]);

    const handleNext = (formData: ShockwaveFormValues) => {
        markStepComplete(currentStep);
        nextStep();
        const nextRoute = getNextStepRoute(currentStep, { templateId });
        if (nextRoute) {
            const nextSettings = {
                ...search,
                ...formData,
                shockwave_id: search.shockwave_id || s.uuidv4(),
            };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            void navigate({
                ...nextRoute,
                search: wizardSearchPayload,
            });
        }
    };

    const handlePrevious = () => {
        const prevRoute = getPreviousStepRoute(currentStep, { templateId });
        if (prevRoute) {
            const nextSettings = { ...search, ...form.getValues() };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            void navigate({
                ...prevRoute,
                search: wizardSearchPayload,
            });
        }
    };

    return (
        <c.ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleNext)}>
                <div className="space-y-6">
                    <div>
                        <h2 className="mb-4 font-semibold text-xl">Shockwave Treatment</h2>
                        <p className="mb-6 text-slate-500 dark:text-slate-300">
                            Add clinician details for the shockwave treatment
                        </p>
                    </div>

                    <LockableField field="referring_clinician_id">
                        <LockableFieldLabel htmlFor="referring_clinician_id">Referring Clinician</LockableFieldLabel>
                        <StaffUserComboBox
                            autoFocus
                            name="referring_clinician_id"
                            description="The clinician who requests the shockwave treatment"
                            options={clinicianOptions}
                            clearable={true}
                            addOption={<InviteForm />}
                        />
                    </LockableField>
                    <LockableField field="shockwaving_clinician_id">
                        <LockableFieldLabel htmlFor="shockwaving_clinician_id">Shockwave Clinician</LockableFieldLabel>
                        <StaffUserComboBox
                            name="shockwaving_clinician_id"
                            description="The clinician who performs the shockwave treatment"
                            options={clinicianOptions}
                            clearable={true}
                            addOption={<InviteForm />}
                        />
                    </LockableField>
                </div>

                <TemplateStepFooter onBack={handlePrevious} submitPrimary />
            </form>
        </c.ui.Form>
    );
};

const shockwaveSearchSchema = templateSettingsSchema.partial().pick({
    referring_clinician_id: true,
    shockwaving_clinician_id: true,
    shockwave_id: true,
});

export const Route = createFileRoute("/_app/templates/$templateId/events/shockwave/")({
    component: ShockwaveFormFieldsPage,
    validateSearch: (search) => shockwaveSearchSchema.parse(search),
});
